import { LocationVarKey, ScheduleSlug } from "@swivl-mono/types";
import { useMemo } from "react";

import { SearchableLabel } from "../../../Components/Worksheets/Types/Worksheet.Interface";
import { usePropertiesGetLocationIds } from "../../../Hooks/usePropertiesGetLocationIds";
import { Member } from "../../../Models/Member/Member.Current";
import { AddScheduleGroup } from "../../../Views/Properties/CortexV3/Components/ScheduleEditor/AddScheduleGroup";
import { HolidaysSection } from "../../../Views/Properties/CortexV3/Components/ScheduleEditor/HolidaysSection";
import { LocationTimezoneSection } from "../../../Views/Properties/CortexV3/Components/ScheduleEditor/LocationTimezoneSection";
import { ScheduleSlugSection } from "../../../Views/Properties/CortexV3/Components/ScheduleEditor/ScheduleSlugSection";
import type { PageScope } from "../../../Views/Properties/CortexV3/Components/ScheduleEditor/Scope";
import { UnmannedFacilityNote } from "../../../Views/Properties/CortexV3/Components/ScheduleEditor/UnmannedFacilityNote";
import { useScheduleGroups } from "../../../Views/Properties/CortexV3/Components/ScheduleEditor/useScheduleGroups";

/**
 * Details v3 → Hours page. One page per scope (org / group / location)
 * surfacing every schedule for that scope: timezone (location only),
 * per-slug weekly grids + exceptions (office-hours, gate-access,
 * call-center), and the holiday rule list.
 *
 * Inheritance is rendered with InheritancePill — at the location scope a
 * row inherited from the org or group is shown as read-only with an
 * "Override" affordance that clones it into a location-scoped row.
 *
 * Per Chuck (2026-05-02): hours moved here OUT of Cortex. Cortex retains the
 * prose access-policy section for nuance the structured grid can't capture
 * ("after-hours access requires a $25 fee + signed addendum"). The bot's
 * `get_location_open_status` tool reads exclusively from the structured
 * Schedule rows.
 */

// Touched vars (for the worksheet harness's "applied vars" badge — purely
// informational; the actual data lives in `schedule` / `schedule_exception`
// / `holiday_rule` tables, NOT in location_variable rows).
export const PAGE_KEYS: LocationVarKey[] = [LocationVarKey.CortexFacilityTimezone];

interface ScheduleSection {
  slug: ScheduleSlug;
  label: string;
  caption: string;
}

// Rendered below AND exported as SEARCH_LABELS — one array, so the two can't drift.
const SCHEDULE_SECTIONS: ScheduleSection[] = [
  { slug: "office-hours", label: "Office Hours", caption: "When staff are on site to answer the door and the phone." },
  { slug: "gate-access", label: "Gate Access", caption: "When tenants can reach their units." },
  { slug: "call-center", label: "Call Center Hours", caption: "When calls route to your call center." },
  {
    slug: "sequence",
    label: "Sequence Sending Hours",
    caption:
      "When automated reminders (past due, move in/out, abandoned cart) may message tenants. A closed day waits for the next open one; sends never leave the 8:00 AM–9:00 PM legal window."
  }
];

export const SEARCH_LABELS: SearchableLabel[] = [
  { title: "Hours", snippet: "When this facility is open, plus holidays and one-off changes." },
  ...SCHEDULE_SECTIONS.map((s) => ({ title: s.label, snippet: s.caption })),
  { title: "Timezone" },
  { title: "Holidays" }
];

const CUSTOM_GROUP_CAPTION = "A schedule group created for this company. Sequences and escalation groups can follow it.";

export default function HoursPage(): React.ReactElement {
  const page = usePropertiesGetLocationIds();
  const scope: PageScope = page.locationId
    ? { kind: "location", groupId: page.groupId, locationId: page.locationId }
    : page.groupId
      ? { kind: "group", groupId: page.groupId }
      : { kind: "org" };
  const scheduleGroups = useScheduleGroups(Member.orgId);

  // Built-ins keep their captions; custom groups (decision 50) follow, company-wide, in label order.
  const sections = useMemo(
    () =>
      scheduleGroups.groups.map((group) => {
        const builtIn = SCHEDULE_SECTIONS.find((section) => section.slug === group.slug);
        return { slug: group.slug, label: group.label, caption: builtIn?.caption ?? CUSTOM_GROUP_CAPTION, custom: !group.builtIn };
      }),
    [scheduleGroups.groups]
  );

  return (
    // Same container as CortexTopicWorksheet so the cards line up with the other
    // Details v3 pages and with the banner above them. Hours doesn't run through
    // that worksheet, so it has to state the width itself — keep the two in sync.
    <div className="mx-auto flex max-w-[960px] flex-col gap-4 p-6">
      {/* Mirrors CortexTopicWorksheet's page header. No mb-4 here — the
          container's gap-4 already provides that spacing. */}
      <header className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="h-3 w-1 shrink-0 bg-grey-900" />
            <h2 className="text-base font-semibold text-grey-900">Hours</h2>
          </div>
          <p className="pl-3 text-base text-grey-600">When this facility is open, plus holidays and one-off changes to specific dates.</p>
        </div>
      </header>

      <LocationTimezoneSection scope={scope} />
      {sections.map((section) => (
        <ScheduleSlugSection
          key={section.slug}
          slug={section.slug}
          label={section.label}
          caption={
            // Office hours imply staff on site, so point unmanned locations at the
            // Access Procedures toggle right where that assumption is stated.
            section.slug === "office-hours" && page.locationId ? (
              <>
                {section.caption} <UnmannedFacilityNote groupId={page.groupId} locationId={page.locationId} preamble={page.preamble} />
              </>
            ) : (
              section.caption
            )
          }
          scope={scope}
          onRenameGroup={section.custom && scope.kind === "org" ? (label) => scheduleGroups.renameGroup(section.slug, label) : undefined}
          onDeleteGroup={section.custom && scope.kind === "org" ? () => scheduleGroups.deleteGroup(section.slug) : undefined}
        />
      ))}
      {scope.kind === "org" && <AddScheduleGroup onAdd={scheduleGroups.addGroup} />}
      {scheduleGroups.error && <p className="body-sm rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-red-700">{scheduleGroups.error}</p>}
      <HolidaysSection scope={scope} groups={scheduleGroups.groups} />
    </div>
  );
}
